import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { cleanText, isCleanText } from '@/utils/sanitize';
import { X, Loader2, Pin, Tag } from 'lucide-react';
import { forumService } from '@/services/forum.service';
import type { ForumThreadDto, ThreadFormData } from '@/types/forum.types';
import toast from 'react-hot-toast';

interface ThreadFormProps {
  courseId: string;
  thread?: ForumThreadDto;
  onClose: () => void;
  onSuccess: (thread: ForumThreadDto) => void;
}

export default function ThreadForm({ courseId, thread, onClose, onSuccess }: ThreadFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const isEditing = !!thread;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ThreadFormData>({
    defaultValues: {
      title: thread?.title || '',
      description: thread?.description || '',
      category: thread?.category || '',
      tags: thread?.tags || [],
      is_pinned: thread?.is_pinned || false,
    },
  });

  const tags = watch('tags') || [];

  // Handle tag addition
  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = tagInput.trim();
      if (tag && !tags.includes(tag)) {
        setValue('tags', [...tags, tag]);
        setTagInput('');
      }
    }
  };

  // Handle tag removal
  const handleRemoveTag = (tagToRemove: string) => {
    setValue('tags', tags.filter(t => t !== tagToRemove));
  };

  // Submit handler
  const onSubmit = async (data: ThreadFormData) => {
    try {
      setSubmitting(true);

      let result: ForumThreadDto;

      if (isEditing && thread) {
        // Update existing thread
        result = await forumService.updateThread(thread._id, {
          title: cleanText(data.title),
          description: cleanText(data.description),
          category: data.category ? cleanText(data.category) : undefined,
          tags: (data.tags || []).map((t: string) => cleanText(t)),
          is_pinned: data.is_pinned,
        });
      } else {
        // Create new thread
        result = await forumService.createThread({
          course_id: courseId,
          title: cleanText(data.title),
          description: cleanText(data.description),
          category: data.category ? cleanText(data.category) : undefined,
          tags: (data.tags || []).map((t: string) => cleanText(t)),
          is_pinned: data.is_pinned,
        });
      }

      onSuccess(result);
    } catch (error: any) {
      toast.error(error?.message || `Failed to ${isEditing ? 'update' : 'create'} thread`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? 'Edit Thread' : 'Create New Thread'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                id="title"
                type="text"
                {...register('title', {
                  required: 'Title is required',
                  minLength: { value: 3, message: 'Title must be at least 3 characters' },
                  maxLength: { value: 200, message: 'Title must not exceed 200 characters' },
                  validate: (v?: string) =>
                    isCleanText(v) || 'Please use only letters, numbers and standard punctuation',
                })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-indigo-500 focus:border-transparent ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter thread title..."
              />
              {errors.title && (
                <p className="mt-1 text-xs text-red-500">{errors.title.message}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Discussion<span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                rows={6}
                {...register('description', {
                  required: 'Description is required',
                  minLength: { value: 10, message: 'Description must be at least 10 characters' },
                })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-brand-indigo-500 focus:border-transparent resize-none ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Describe your thread in detail..."
              />
              {errors.description && (
                <p className="mt-1 text-xs text-red-500">{errors.description.message}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <input
                id="category"
                type="text"
                {...register('category', {
                  validate: (v?: string) =>
                    isCleanText(v) || 'Please use only letters, numbers and standard punctuation',
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-indigo-500 focus:border-transparent"
                placeholder="e.g., General, Question, Discussion..."
              />
            </div>

            {/* Tags */}
            {/* <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2 p-2 min-h-[42px] border border-gray-300 rounded-lg bg-gray-50">
                  {tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-brand-indigo-100 text-brand-indigo-700 text-sm rounded"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 text-brand-indigo-500 hover:text-brand-indigo-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  id="tags"
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleAddTag}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-indigo-500 focus:border-transparent"
                  placeholder="Type a tag and press Enter..."
                />
                <p className="text-xs text-gray-500">Press Enter or comma to add a tag</p>
              </div>
            </div> */}

            {/* Pin Thread Checkbox */}
            <div className="flex items-center gap-2">
              <input
                id="is_pinned"
                type="checkbox"
                {...register('is_pinned')}
                className="w-4 h-4 text-brand-indigo-600 border-gray-300 rounded focus:ring-brand-indigo-500"
              />
              <label htmlFor="is_pinned" className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <Pin className="w-4 h-4 text-brand-blue-600" />
                Pin this thread to the top
              </label>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={submitting}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-indigo-600 rounded-lg hover:bg-brand-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Saving...' : isEditing ? 'Update Thread' : 'Create Thread'}
          </button>
        </div>
      </div>
    </div>
  );
}
